import React from "react";
import { formatHkt, formatHktDate } from "@shared/lib/datetime";
import type { AuditEvent } from "@shared/api/audit";

// F-E Phase 4b — audit event table. 5 default columns + optional extras
// (toggled by the page's Columns menu). Row click opens the detail drawer;
// clicking an actor/action cell adds it to the filters (click-to-filter).

export type ExtraColumn =
  "correlationId" | "requestIp" | "userAgent" | "denyReason";

export const EXTRA_COLUMN_LABELS: Record<ExtraColumn, string> = {
  correlationId: "Correlation ID",
  requestIp: "Request IP",
  userAgent: "User agent",
  denyReason: "Deny reason",
};

interface Props {
  events: AuditEvent[];
  extras: ExtraColumn[];
  onSelect: (event: AuditEvent) => void;
  onFilterActor: (actorId: string) => void;
  onFilterAction: (action: string) => void;
}

function decisionCell(decision: AuditEvent["decision"]): React.ReactNode {
  if (decision === "allow") return <span className="text-green-600">✓</span>;
  if (decision === "deny") return <span className="text-destructive">✗</span>;
  return <span className="text-muted-foreground">—</span>;
}

// Human-readable when the server resolved a label (entity title / username);
// falls back to kind:id for gone records — honest for hard-deleted targets.
function resourceCell(e: AuditEvent): string {
  if (!e.resourceKind) return "—";
  if (e.resourceLabel) return `${e.resourceLabel} (${e.resourceKind})`;
  return e.resourceId ? `${e.resourceKind}:${e.resourceId}` : e.resourceKind;
}

// Change-highlight (2026-07-24): mutations record a field diff in
// before/after — list the changed field names inline; the drawer has the
// full old→new values.
function changedFields(e: AuditEvent): string | null {
  const keys = new Set([
    ...Object.keys(e.before ?? {}),
    ...Object.keys(e.after ?? {}),
  ]);
  return keys.size ? [...keys].sort().join(", ") : null;
}

// Uploads (2026-07-27): asset:add events record what was uploaded —
// summarize as "uploaded: cover.png (image)"; full details in the drawer.
function uploadSummary(e: AuditEvent): string | null {
  if (!e.action.endsWith(":asset:add")) return null;
  const md = (e.metadata ?? {}) as { filename?: string; kind?: string };
  if (!md.filename) return null; // pre-enrichment rows stay bare
  return `${md.filename}${md.kind ? ` (${md.kind})` : ""}`;
}

// Export scope (2026-07-24): audit:export events record their filters +
// row count in metadata — summarize inline; the drawer has the full JSON.
// stats:export records the same {filters, rows} shape (2026-10-08).
const SCOPED_EXPORTS = new Set(["audit:export", "stats:export"]);

function hktDay(value: string): string {
  return value.includes("T") ? formatHktDate(value) : value.slice(0, 10);
}

function exportSummary(e: AuditEvent): string | null {
  if (!SCOPED_EXPORTS.has(e.action)) return null;
  const md = (e.metadata ?? {}) as {
    filters?: Record<string, string>;
    rows?: number;
    truncated?: boolean;
  };
  const parts: string[] = [];
  if (typeof md.rows === "number") {
    parts.push(
      `${md.rows.toLocaleString()} rows${md.truncated ? " (truncated)" : ""}`,
    );
  }
  for (const [k, v] of Object.entries(md.filters ?? {})) {
    // dates show as their Hong Kong day (F-AC): stats exports record the day
    // itself, audit exports a UTC instant; other filters show as key=value
    parts.push(`${k}=${k === "from" || k === "to" ? hktDay(String(v)) : v}`);
  }
  return parts.length ? parts.join(" · ") : null;
}

const th = "text-left px-4 py-3 text-sm font-medium whitespace-nowrap";
const td = "px-4 py-3 text-sm align-top";

const AuditTable: React.FC<Props> = ({
  events,
  extras,
  onSelect,
  onFilterActor,
  onFilterAction,
}) => (
  <div className="bg-white border border-border rounded-lg overflow-x-auto">
    <table className="w-full" data-testid="audit-table">
      <thead className="bg-muted/50 border-b border-border">
        <tr>
          <th className={th}>When (HKT)</th>
          <th className={th}>Actor</th>
          <th className={th}>Action</th>
          <th className={th}>Resource</th>
          <th className={th}>✓/✗</th>
          {extras.map((c) => (
            <th key={c} className={th}>
              {EXTRA_COLUMN_LABELS[c]}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-border">
        {events.map((e) => (
          <tr
            key={e.id}
            onClick={() => onSelect(e)}
            className="cursor-pointer hover:bg-muted/30"
            data-testid={`audit-row-${e.id}`}
          >
            <td className={`${td} whitespace-nowrap font-mono text-xs`}>
              {formatHkt(e.ts)}
            </td>
            <td className={td}>
              <button
                type="button"
                className="hover:underline text-left"
                onClick={(ev) => {
                  ev.stopPropagation();
                  onFilterActor(e.actorId);
                }}
                data-testid={`audit-filter-actor-${e.id}`}
              >
                {e.actorName ?? e.actorId}
                {e.actorRole ? ` (${e.actorRole})` : ""}
              </button>
            </td>
            <td className={td}>
              <button
                type="button"
                className="hover:underline text-left font-mono text-xs"
                onClick={(ev) => {
                  ev.stopPropagation();
                  onFilterAction(e.action);
                }}
                data-testid={`audit-filter-action-${e.id}`}
              >
                {e.action}
              </button>
              {changedFields(e) && (
                <div
                  className="text-[11px] text-muted-foreground max-w-[16rem] truncate"
                  title={`Changed: ${changedFields(e)}`}
                  data-testid={`audit-changed-${e.id}`}
                >
                  changed: {changedFields(e)}
                </div>
              )}
              {exportSummary(e) && (
                <div
                  className="text-[11px] text-muted-foreground max-w-[16rem] truncate"
                  title={`Exported: ${exportSummary(e)}`}
                  data-testid={`audit-export-scope-${e.id}`}
                >
                  exported: {exportSummary(e)}
                </div>
              )}
              {uploadSummary(e) && (
                <div
                  className="text-[11px] text-muted-foreground max-w-[16rem] truncate"
                  title={`Uploaded: ${uploadSummary(e)}`}
                  data-testid={`audit-uploaded-${e.id}`}
                >
                  uploaded: {uploadSummary(e)}
                </div>
              )}
            </td>
            <td className={`${td} text-xs`}>{resourceCell(e)}</td>
            <td className={td}>{decisionCell(e.decision)}</td>
            {extras.map((c) => (
              <td
                key={c}
                className={`${td} font-mono text-xs max-w-xs truncate`}
              >
                {(e[c] as string | null | undefined) || "—"}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default AuditTable;
